import React from 'react'
import { connect } from 'react-redux'
import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, LineChart, Line,
  PieChart, Pie, Cell,
} from 'recharts'
import { API_ROOT } from '../config'
import '../styles/DashboardView.css'

// ─── Helpers ─────────────────────────────────────────────────────
function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

function monthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function monthEnd(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

function fmtDate(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate)
  if (isNaN(d)) return '—'
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtDateTime(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate)
  if (isNaN(d)) return '—'
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const ACTION_LABELS = {
  PAGE_VIEW: 'Vista de página',
  CLICK:     'Clic',
  LOGIN:     'Login',
  LOGOUT:    'Logout',
}

const ACTION_ICONS = {
  PAGE_VIEW: 'fa-eye',
  CLICK:     'fa-mouse-pointer',
  LOGIN:     'fa-sign-in-alt',
  LOGOUT:    'fa-sign-out-alt',
}

const ACTION_COLORS = {
  PAGE_VIEW: '#2563eb',
  CLICK:     '#7c3aed',
  LOGIN:     '#059669',
  LOGOUT:    '#d97706',
}

const ROLE_COLORS = {
  ADMIN:    '#2563eb',
  CUSTOMER: '#059669',
}

const CHART_COLORS = [
  '#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626',
  '#0891b2', '#be185d', '#4f46e5', '#15803d', '#ca8a04',
  '#0e7490', '#9d174d', '#1d4ed8', '#065f46', '#92400e',
  '#7f1d1d', '#1e3a5f', '#4c1d95', '#064e3b', '#78350f',
]

// ─── Shared tooltip ──────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div style={{
      background: 'rgba(255,255,255,0.97)',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: 12,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      pointerEvents: 'none',
    }}>
      {label && <div style={{ fontWeight: 700, marginBottom: 6, color: '#111827' }}>{label}</div>}
      {payload.map((entry, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: i === 0 ? 0 : 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: entry.color || entry.fill, flexShrink: 0, display: 'inline-block' }} />
          <span style={{ color: '#6b7280' }}>{entry.name}:</span>
          <span style={{ fontWeight: 600, color: '#111827' }}>{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

// ─── KPI card ────────────────────────────────────────────────────
function KpiCard({ icon, value, label, colorClass }) {
  return (
    <div className={`dashboard-kpi-card ${colorClass}`}>
      <div className="kpi-icon"><i className={`fas ${icon}`} /></div>
      <div className="kpi-content">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value">{value === null || value === undefined ? '—' : value.toLocaleString()}</div>
      </div>
    </div>
  )
}

// ─── Section card ────────────────────────────────────────────────
function Section({ title, icon, children, style }) {
  return (
    <div className="dashboard-card-section" style={{ marginBottom: 18, ...style }}>
      <div className="card-header-custom" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <i className={`fas ${icon}`} style={{ color: '#2563eb' }} />
        <span>{title}</span>
      </div>
      <div className="card-body-custom">{children}</div>
    </div>
  )
}

// ─── Top pages table ─────────────────────────────────────────────
function TopPagesTable({ data }) {
  if (!data || data.length === 0) return <p style={{ color: '#6b7280', fontSize: 13 }}>Sin datos</p>
  const max = data[0]?.count || 1
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600, width: 28 }}>#</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Página</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280', fontWeight: 600, width: 80 }}>Visitas</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', width: 160 }}></th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '6px 8px', color: '#9ca3af', fontWeight: 700 }}>{i + 1}</td>
            <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#1d4ed8' }}>{row.page}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{row.count.toLocaleString()}</td>
            <td style={{ padding: '6px 8px' }}>
              <div style={{ height: 8, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(row.count / max) * 100}%`, background: '#2563eb', borderRadius: 4 }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ─── Top users table ─────────────────────────────────────────────
function TopUsersTable({ data }) {
  if (!data || data.length === 0) return <p style={{ color: '#6b7280', fontSize: 13 }}>Sin datos</p>
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600, width: 28 }}>#</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Usuario</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Rol</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Tipo</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Eventos</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Última actividad</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '6px 8px', color: '#9ca3af', fontWeight: 700 }}>{i + 1}</td>
            <td style={{ padding: '6px 8px', fontWeight: 600 }}>{row.name}</td>
            <td style={{ padding: '6px 8px' }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                background: row.role === 'ADMIN' ? '#dbeafe' : '#d1fae5',
                color: row.role === 'ADMIN' ? '#1d4ed8' : '#065f46',
              }}>{row.role}</span>
            </td>
            <td style={{ padding: '6px 8px', color: '#6b7280' }}>{row.type || '—'}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{row.count.toLocaleString()}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>{fmtDateTime(row.lastSeen)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ─── Recent logins table ─────────────────────────────────────────
function RecentLoginsTable({ data }) {
  if (!data || data.length === 0) return <p style={{ color: '#6b7280', fontSize: 13 }}>Sin datos</p>
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Usuario</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Rol</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Tipo</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Fecha / Hora</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '6px 8px', fontWeight: 600 }}>{row.name}</td>
            <td style={{ padding: '6px 8px' }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                background: row.role === 'ADMIN' ? '#dbeafe' : '#d1fae5',
                color: row.role === 'ADMIN' ? '#1d4ed8' : '#065f46',
              }}>{row.role}</span>
            </td>
            <td style={{ padding: '6px 8px', color: '#6b7280' }}>{row.type || '—'}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>{fmtDateTime(row.ts)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const EXCLUDE_NAME = 'vacway' // VACWAY TECH internal users

// ─── Main component ──────────────────────────────────────────────
class ActivityDashboardView extends React.Component {
  constructor(props) {
    super(props)
    const today = new Date()
    this.state = {
      // fetch period (committed — triggers load)
      startDate: localIso(monthStart(today)),
      endDate:   localIso(monthEnd(today)),
      // staged date inputs (edited in the advanced panel, applied on "Cargar")
      tempStart: localIso(monthStart(today)),
      tempEnd:   localIso(monthEnd(today)),
      // UI
      showAdvancedFilters: false,
      // real-time client-side filters (no fetch)
      roleFilter:   '',     // '' = all, 'ADMIN', 'CUSTOMER'
      nameFilter:   '',     // free-text on user name
      uidFilter:    '',     // set when an exact user is picked — triggers re-fetch
      hideVacway:   true,   // exclude VACWAY TECH internal users
      // data
      loading: false,
      error: null,
      kpiData: null,       // { totalEvents, totalPageViews, totalLogins, uniqueUsers, totalClicks, totalLogouts }
      timelineData: [],    // [{ date, total }]
      topPages: [],        // [{ page, count }]
      topUsers: [],        // [{ uid, name, role, type, count, lastSeen }]
      actionDist: [],      // [{ action, count }]
      recentLogins: [],    // raw log entries, action=LOGIN, sorted desc
    }
  }

  componentDidMount() {
    this.load()
  }

  buildQS(extra = {}) {
    const { startDate, endDate, uidFilter, roleFilter } = this.state
    const params = new URLSearchParams({
      from:  startDate,
      to:    endDate,
      limit: 20,
      ...(uidFilter   ? { uid:  uidFilter  } : {}),
      ...(roleFilter  ? { role: roleFilter } : {}),
      ...extra,
    })
    return params.toString()
  }

  fetchJson(endpoint) {
    const token = localStorage.getItem('token') || ''
    return fetch(`${API_ROOT}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    })
  }

  load = async () => {
    this.setState({ loading: true, error: null })
    try {
      const [pageRes, userRes, actionRes, dayRes, rawLogins] = await Promise.all([
        this.fetchJson(`activitylogs/report?${this.buildQS({ groupBy: 'page',   limit: 20 })}`),
        this.fetchJson(`activitylogs/report?${this.buildQS({ groupBy: 'user',   limit: 20 })}`),
        this.fetchJson(`activitylogs/report?${this.buildQS({ groupBy: 'action', limit: 10 })}`),
        this.fetchJson(`activitylogs/report?${this.buildQS({ groupBy: 'day',    limit: 400 })}`),
        // recent logins: raw log, action forced to LOGIN, limit 30
        this.fetchJson(`activitylogs?${new URLSearchParams({
          from:   this.state.startDate,
          to:     this.state.endDate,
          action: 'LOGIN',
          limit:  50,
          ...(this.state.uidFilter  ? { uid:  this.state.uidFilter  } : {}),
          ...(this.state.roleFilter ? { role: this.state.roleFilter } : {}),
        }).toString()}`),
      ])

      const topPages   = (pageRes.results   || [])
      const topUsers   = (userRes.results   || [])
      const actionDist = (actionRes.results || [])
      const dayResults = (dayRes.results    || [])
      const recentLogins = (rawLogins.logs  || [])

      // build timeline from day-grouped results.
      // The API returns { date: 'YYYY-MM-DD', count } — date is already a string.
      const dayMap = {}
      dayResults.forEach(({ date, count }) => {
        const iso = typeof date === 'string' ? date.slice(0, 10)
          : (date ? new Date(date).toISOString().slice(0, 10) : null)
        if (!iso) return
        if (!dayMap[iso]) dayMap[iso] = { date: iso }
        dayMap[iso].total = (dayMap[iso].total || 0) + count
      })

      // if actionFilter is not set, we get all actions merged in 'day' groupBy
      // so timeline is just total events per day
      const timelineData = Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date))

      // derive KPIs from actionDist
      const actionMap = {}
      actionDist.forEach(({ action, count }) => { actionMap[action] = count })
      const totalEvents    = actionDist.reduce((s, r) => s + (r.count || 0), 0)
      const totalPageViews = actionMap['PAGE_VIEW'] || 0
      const totalLogins    = actionMap['LOGIN']     || 0
      const totalClicks    = actionMap['CLICK']     || 0
      const totalLogouts   = actionMap['LOGOUT']    || 0
      const uniqueUsers    = topUsers.length  // approximate — top 20 users

      // role distribution: derive from topUsers
      const roleCountMap = {}
      topUsers.forEach(u => { roleCountMap[u.role] = (roleCountMap[u.role] || 0) + u.count })
      const roleDist = Object.entries(roleCountMap).map(([role, count]) => ({ role, count }))

      this.setState({
        topPages,
        topUsers,
        actionDist,
        timelineData,
        recentLogins,
        kpiData: { totalEvents, totalPageViews, totalLogins, totalClicks, totalLogouts, uniqueUsers },
        loading: false,
      })
    } catch (err) {
      this.setState({ loading: false, error: 'Error al cargar los datos de actividad.' })
    }
  }

  applyPeriod = () => {
    const { tempStart, tempEnd } = this.state
    this.setState({ startDate: tempStart, endDate: tempEnd }, this.load)
  }

  render() {
    const {
      loading, error,
      tempStart, tempEnd,
      startDate, endDate,
      showAdvancedFilters,
      roleFilter, nameFilter, hideVacway,
      kpiData, timelineData, topPages, topUsers, actionDist, recentLogins,
    } = this.state

    if (error) return <ErrorMessage message={error} />

    // ── Real-time client-side filtering (no fetch) ──
    const matchUser = u =>
      (!roleFilter || u.role === roleFilter) &&
      (!nameFilter || (u.name || '').toLowerCase().includes(nameFilter.toLowerCase())) &&
      (!hideVacway || !(u.name || '').toLowerCase().includes(EXCLUDE_NAME))

    const filteredUsers  = topUsers.filter(matchUser)
    const filteredLogins = recentLogins.filter(u =>
      (!roleFilter || u.role === roleFilter) &&
      (!nameFilter || (u.name || '').toLowerCase().includes(nameFilter.toLowerCase())) &&
      (!hideVacway || !(u.name || '').toLowerCase().includes(EXCLUDE_NAME))
    )

    // recompute role distribution from filtered users
    const roleCountMap = {}
    filteredUsers.forEach(u => { roleCountMap[u.role] = (roleCountMap[u.role] || 0) + u.count })
    const roleDist = Object.entries(roleCountMap).map(([role, count]) => ({ role, count }))

    // recompute KPIs from filtered data (what we can derive client-side)
    const filteredKpi = kpiData ? {
      totalEvents:    filteredUsers.reduce((s, u) => s + (u.count || 0), 0),
      uniqueUsers:    filteredUsers.length,
      totalLogins:    filteredLogins.length,
      // page views / clicks / logouts: not available per-user from pre-aggregated data
      totalPageViews: null,
      totalClicks:    null,
      totalLogouts:   null,
    } : null

    // name autocomplete options
    const nameOptions = topUsers
      .map(u => u.name)
      .filter(Boolean)
      .filter(n => !hideVacway || !n.toLowerCase().includes(EXCLUDE_NAME))
      .sort()

    return (
      <div className="dashboard-view">
        {/* ── Header ── */}
        <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="dashboard-title-section">
            <div className="dashboard-title">
              <i className="fas fa-chart-line" style={{ color: '#2563eb' }} />
              Dashboard de Actividad
            </div>
            <div className="small text-muted mt-1">
              {fmtDate(startDate)} — {fmtDate(endDate)}
            </div>
          </div>

          <div className="dashboard-actions">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
              {/* Real-time filters — no fetch */}
              <select
                className="dashboard-filter-input"
                value={roleFilter}
                onChange={e => this.setState({ roleFilter: e.target.value }, this.load)}
                style={{ minWidth: 140 }}
              >
                <option value="">Todos los roles</option>
                <option value="ADMIN">ADMIN</option>
                <option value="CUSTOMER">CUSTOMER</option>
              </select>

              <input
                type="text"
                list="activity-name-options"
                className="dashboard-filter-input"
                placeholder="Filtrar por nombre…"
                value={nameFilter}
                onChange={e => {
                  const val = e.target.value
                  // check if the typed value exactly matches a known user
                  const matched = topUsers.find(u => u.name === val)
                  const newUid = matched ? matched.uid : ''
                  const needsRefetch = newUid !== this.state.uidFilter
                  this.setState({ nameFilter: val, uidFilter: newUid }, () => {
                    if (needsRefetch) this.load()
                  })
                }}
                style={{ minWidth: 180 }}
              />
              <datalist id="activity-name-options">
                {nameOptions.map(n => <option key={n} value={n} />)}
              </datalist>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label className="small text-muted" style={{ margin: 0, whiteSpace: 'nowrap' }}>Ocultar VACWAY</label>
                <label className="dashboard-switch" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={hideVacway}
                    onChange={e => this.setState({ hideVacway: e.target.checked })}
                  />
                  <span className="slider round" />
                </label>
              </div>
            </div>

            {/* Toggle advanced (fetch) filters */}
            <button
              type="button"
              className="dashboard-filter-input toggle-filters"
              onClick={() => this.setState({ showAdvancedFilters: !showAdvancedFilters })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              {showAdvancedFilters
                ? <><i className="fas fa-chevron-up" style={{ marginRight: 6 }} />Ocultar filtros</>
                : <><i className="fas fa-chevron-down" style={{ marginRight: 6 }} />Mostrar filtros</>}
            </button>
          </div>
        </div>

        {/* ── Advanced (fetch) filter panel ── */}
        {showAdvancedFilters && (
          <div className="dashboard-header mb-3" style={{ padding: '12px 20px', justifyContent: 'center' }}>
            <div className="d-flex align-items-center flex-wrap justify-content-center" style={{ gap: 8 }}>
              <label style={{ fontSize: 12, color: '#6b7280' }}>Desde</label>
              <input
                type="date"
                className="dashboard-filter-input"
                value={tempStart}
                onChange={e => this.setState({ tempStart: e.target.value })}
                style={{ width: 150 }}
              />
              <label style={{ fontSize: 12, color: '#6b7280' }}>Hasta</label>
              <input
                type="date"
                className="dashboard-filter-input"
                value={tempEnd}
                onChange={e => this.setState({ tempEnd: e.target.value })}
                style={{ width: 150 }}
              />
              <button
                className="btn btn-vw ml-2"
                disabled={loading || !tempStart || !tempEnd}
                onClick={this.applyPeriod}
              >
                {loading ? 'Cargando…' : 'Cargar'}
              </button>
            </div>
          </div>
        )}

        {loading && <LoadingProgress />}

        {/* ── KPIs ── */}
        {filteredKpi && (
          <div className="dashboard-kpi-wrapper" style={{ marginBottom: 18 }}>
            <div className="dashboard-kpi-row">
              <KpiCard icon="fa-bolt"          value={filteredKpi.totalEvents}    label="Eventos totales"   colorClass="total" />
              <KpiCard icon="fa-sign-in-alt"   value={filteredKpi.totalLogins}    label="Logins"            colorClass="green-block" />
              <KpiCard icon="fa-users"         value={filteredKpi.uniqueUsers}    label="Usuarios activos"  colorClass="income-month" />
            </div>
          </div>
        )}

        {/* ── Timeline + pie row ── */}
        <div className="dashboard-graphs-grid-activity">
          <Section title="Actividad diaria" icon="fa-chart-line">
            {timelineData.length === 0
              ? <p style={{ color: '#6b7280', fontSize: 13 }}>Sin datos para el período seleccionado.</p>
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={timelineData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v ? v.slice(5) : ''} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2} dot={false} name="Eventos" />
                  </LineChart>
                </ResponsiveContainer>
              )
            }
          </Section>

          <Section title="Distribución de eventos" icon="fa-pie-chart">
            {actionDist.length === 0
              ? <p style={{ color: '#6b7280', fontSize: 13 }}>Sin datos</p>
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={actionDist.map(r => ({ name: ACTION_LABELS[r.action] || r.action, value: r.count }))}
                      dataKey="value"
                      cx="50%" cy="50%"
                      innerRadius={50} outerRadius={80}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {actionDist.map((r, i) => (
                        <Cell key={i} fill={ACTION_COLORS[r.action] || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val, name) => [val.toLocaleString(), name]} />
                  </PieChart>
                </ResponsiveContainer>
              )
            }
          </Section>
        </div>

        {/* ── Top pages + top users ── */}
        <div className="dashboard-graphs-grid-activity">
          <Section title="Top páginas visitadas" icon="fa-globe">
            <div style={{ marginBottom: 12 }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  layout="vertical"
                  data={topPages.slice(0, 10).map(r => ({ page: r.page.replace(/^\//, '') || '/', count: r.count }))}
                  margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="page" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" name="Visitas" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <TopPagesTable data={topPages} />
          </Section>

          <Section title="Top usuarios por actividad" icon="fa-user-clock">
            <TopUsersTable data={filteredUsers} />
          </Section>
        </div>

        {/* ── Role distribution + recent logins ── */}
        <div className="dashboard-graphs-grid-activity">
          
          <Section title="Logins recientes" icon="fa-sign-in-alt">
            <RecentLoginsTable data={filteredLogins} />
          </Section>
          
          <Section title="Actividad por rol" icon="fa-shield-alt">
            {roleDist.length === 0
              ? <p style={{ color: '#6b7280', fontSize: 13 }}>Sin datos</p>
              : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={roleDist.map(r => ({ name: r.role, value: r.count }))}
                        dataKey="value"
                        cx="50%" cy="50%"
                        innerRadius={45} outerRadius={70}
                        paddingAngle={3}
                      >
                        {roleDist.map((r, i) => (
                          <Cell key={i} fill={ROLE_COLORS[r.role] || CHART_COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val, name) => [val.toLocaleString(), name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {roleDist.map((r, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: ROLE_COLORS[r.role] || '#111827' }}>
                          {r.count.toLocaleString()}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{r.role}</div>
                      </div>
                    ))}
                  </div>
                </>
              )
            }
          </Section>
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  role: state.auth.role,
  type: state.auth.type,
})

export default connect(mapStateToProps)(ActivityDashboardView)
